import {
  EXPLICIT_ABSOLUTE_LEADING_SUFFIX,
  LEADING_RATIO_BAND_MAX,
  parseStoredLeading,
} from '@/lib/text/leading'

/**
 * Keeps a COMPUTED (Narrow/Wide/Metric preset) leading value out of the ratio band, where a bare
 * stored value is re-read as a MULTIPLIER rather than a pt length (`LEADING_RATIO_BAND_MAX`,
 * `paragraph-block-styles.ts`). Without this, `computeNarrowLeadingPt(5)` emits `4` — which comes
 * back as 4× the point size, a 20pt pitch on 5pt text. Reachable at any hand-typed font size below
 * ~6pt; the size chips all start at 8pt.
 *
 * Scoped to the three preset formulas below — a hand-typed or dragged Leading value
 * (`hooks/use-leading-field.ts`) is written via `toStoredLeadingString` instead, which marks a value
 * in the band explicit rather than pushing it out of the band, so the applied result matches what
 * was actually entered (e.g. a literal `0`) rather than being silently substituted.
 */
export function outOfLeadingRatioBand(pt: number): number {
  return Math.max(pt, LEADING_RATIO_BAND_MAX + 0.1)
}

/**
 * Converts a resolved Leading pt value to the string actually written to the wire — the
 * counterpart to `parseStoredLeading` (`paragraph-block-styles.ts`), which this must stay in sync
 * with. A value at or below `LEADING_RATIO_BAND_MAX` (including 0) is marked explicit so it reads
 * back as the absolute length it is, not a legacy ratio multiplier; anything above the band is
 * already unambiguous as a bare number.
 */
export function toStoredLeadingString(pt: number): string {
  return pt <= LEADING_RATIO_BAND_MAX ? `${pt}${EXPLICIT_ABSOLUTE_LEADING_SUFFIX}` : String(pt)
}

/** Narrow leading: fontSize × 1.2 − 2pt, rounded to one decimal place. */
export function computeNarrowLeadingPt(fontSizePt: number): number {
  return outOfLeadingRatioBand(Math.round((fontSizePt * 1.2 - 2) * 10) / 10)
}

/** Wide leading: fontSize × 1.2 + 4pt, rounded to one decimal place. */
export function computeWideLeadingPt(fontSizePt: number): number {
  return outOfLeadingRatioBand(Math.round((fontSizePt * 1.2 + 4) * 10) / 10)
}

/** Metric leading (font-metric-driven default): fontSize × 1.2, rounded to one decimal place. */
export function computeMetricLeadingPt(fontSizePt: number): number {
  return outOfLeadingRatioBand(Math.round(fontSizePt * 1.2 * 10) / 10)
}

/**
 * Which leading chip (if any) `pt` corresponds to for the given font size — used to keep the
 * Narrow/Wide chips highlighted whenever the numeric leading matches their computed value,
 * whether that value came from clicking the chip, typing it manually, or loading stored content.
 */
export function matchLeadingChip(pt: number, fontSizePt: number): 'Narrow' | 'Wide' | null {
  if (pt === computeNarrowLeadingPt(fontSizePt)) return 'Narrow'
  if (pt === computeWideLeadingPt(fontSizePt)) return 'Wide'

  return null
}

/**
 * A stored leading restated for a new point size, or `undefined` when it must stay as it is.
 *
 * Only a Narrow/Wide preset follows the point size: it is defined relative to the font size, so it
 * is recomputed as the same preset for the new size (Narrow stays Narrow). Any other absolute value
 * is one the user typed or dragged, and stays fixed in points (AC6, WMPCM-9899) — rescaling it
 * turned 30pt on 16pt text into 60pt at 32pt. Metric/absent and legacy ratio values are already
 * size-relative and need nothing.
 */
export function restateLeadingPresetForPointSize(
  leading: string | undefined | null,
  fromPointSize: number | undefined,
  toPointSize: number
): string | undefined {
  const parsed = parseStoredLeading(leading)

  if (!parsed || parsed.isRatio) return undefined
  if (!fromPointSize || fromPointSize <= 0 || toPointSize <= 0) return undefined
  if (fromPointSize === toPointSize) return undefined

  const preset = matchLeadingChip(parsed.n, fromPointSize)

  if (preset === 'Narrow') return toStoredLeadingString(computeNarrowLeadingPt(toPointSize))
  if (preset === 'Wide') return toStoredLeadingString(computeWideLeadingPt(toPointSize))

  return undefined
}
